import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { ChevronRight, ChevronDown, Package, Loader2, Maximize2, Minimize2, ZoomIn, ZoomOut, RotateCcw, Search } from "lucide-react";
import { getOnSaleSpuProducts, OnSaleSpuProduct, FORECAST_SEARCH_IDS } from "@/services/api/forecast";
import { cn } from "@/lib/utils";
import * as echarts from 'echarts';

// 脑图节点接口
interface MindMapNode {
  id: string;
  name: string;
  type: 'brand' | 'majorCategory' | 'mediumCategory' | 'minorCategory' | 'mainSpu' | 'spu' | 'product';
  children?: MindMapNode[];
  salesCount?: number;
  data?: OnSaleSpuProduct;
}

// 品牌排序顺序
const BRAND_ORDER = [
  'KIMTRUE且初',
  'ddg',
  'ONCUR安修泽',
  'Aucuz奥札',
  'EVERBAB艾蓓拉',
  '3odice三蒂丝'
];

// ECharts树形节点接口
interface EChartTreeNode {
  name: string;
  value?: number;
  children?: EChartTreeNode[];
  itemStyle?: {
    color?: string;
  };
  label?: {
    show?: boolean;
    position?: string;
    fontSize?: number;
    fontWeight?: string;
  };
  lineStyle?: {
    color?: string;
    width?: number;
  };
  cursor?: string;
  productCode?: string;
  productName?: string;
  collapsed?: boolean;
}

// 品牌选项接口
interface BrandOption {
  code: string;
  name: string;
}

// 品牌数据源
const BRAND_OPTIONS: BrandOption[] = [
  { code: 'B2214', name: 'KIMTRUE且初' },
  { code: 'B00206', name: 'EVERBAB艾蓓拉' },
  { code: 'B02330', name: 'ddg' },
  { code: 'B02333', name: '3odice三蒂丝' },
  { code: 'B02335', name: 'ONCUR安修泽' },
  { code: 'B02407', name: 'Aucuz奥札' },
];

const OnSaleSpuProducts: React.FC = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [mindMapData, setMindMapData] = useState<MindMapNode[]>([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [chartHeight, setChartHeight] = useState<number>(800); // 动态高度
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  // 品牌选择相关状态
  const [selectedBrandCode, setSelectedBrandCode] = useState<string>('B2214'); // 默认选择B2214

  // 获取在售商品数据
  const fetchOnSaleSpuProducts = async () => {
    setLoading(true);
    try {
      const response = await getOnSaleSpuProducts({
        search_id: FORECAST_SEARCH_IDS.ON_SALE_SPU_PRODUCTS,
        page_size: 1000,
        品牌编码: selectedBrandCode // 使用当前选择的品牌编码
      });

      console.log('API响应:', response); // 调试日志

      if ((response.res_status_code === '200' || response.res_status_code === '0') && response.res_content) {
        // 尝试从不同位置获取数据
        let productsData: OnSaleSpuProduct[] = [];

        if (Array.isArray(response.res_content)) {
          productsData = response.res_content;
        } else if (response.res_content.data && Array.isArray(response.res_content.data)) {
          productsData = response.res_content.data;
        } else if (response.res_content.list && Array.isArray(response.res_content.list)) {
          productsData = response.res_content.list;
        } else if (response.res_content.data_list && Array.isArray(response.res_content.data_list)) {
          productsData = response.res_content.data_list;
        }

        console.log('解析到的商品数据:', productsData); // 调试日志
        console.log('第一个商品数据字段:', productsData[0]); // 调试第一个商品的字段
        console.log('第一个商品的所有字段名:', productsData[0] ? Object.keys(productsData[0]) : '无数据'); // 输出所有字段名

        if (productsData.length > 0) {
          // 构建脑图数据结构
          const mindMapData = buildMindMapData(productsData);
          setMindMapData(mindMapData);

          // 计算并设置动态高度
          const optimalHeight = calculateOptimalHeight(mindMapData);
          setChartHeight(optimalHeight);
        } else {
          console.warn('API返回成功但没有数据');
          setMindMapData([]);
          setChartHeight(400); // 重置为默认高度
        }
      } else {
        console.error('API返回非成功状态:', response);
        setMindMapData([]);
        setChartHeight(400); // 重置为默认高度
      }
    } catch (error) {
      console.error('获取在售商品数据失败:', error);
      setMindMapData([]);
      setChartHeight(400); // 重置为默认高度
    } finally {
      setLoading(false);
    }
  };


  // 构建脑图数据结构
  const buildMindMapData = (products: OnSaleSpuProduct[]): MindMapNode[] => {
    const brandMap = new Map<string, MindMapNode>();

    products.forEach((product, index) => {
      // 添加调试日志
      if (index === 0) {
        console.log('产品数据的所有字段:', Object.keys(product));
        console.log('大分类字段值:', product.大分类);
        console.log('中分类字段值:', product.中分类);
        console.log('小分类字段值:', product.小分类);
        console.log('检查是否有其他可能的字段名:', {
          '大分类': product.大分类,
          'bigCategory': product.bigCategory,
          'majorCategory': product.majorCategory,
          '一级分类': product.一级分类,
          '一级': product.一级,
          '分类1': product.分类1
        });
      }

      const { 品牌, 大分类, 中分类, 小分类, 主SPU名称, SPU名称, 产品名称, 销售数量 } = product;

      // 品牌层级
      if (!brandMap.has(品牌)) {
        brandMap.set(品牌, {
          id: `brand_${品牌}`,
          name: 品牌,
          type: 'brand',
          children: [],
          salesCount: 0
        });
      }

      const brandNode = brandMap.get(品牌)!;
      brandNode.salesCount = (brandNode.salesCount || 0) + 销售数量;

      // 处理可能的undefined值，提供默认值
      const majorCategoryName = 大分类 || '未分类';
      const mediumCategoryName = 中分类 || '未分类';
      const minorCategoryName = 小分类 || '未分类';

      // 大分类层级
      let majorCategoryNode = brandNode.children?.find(child => child.name === majorCategoryName);
      if (!majorCategoryNode) {
        majorCategoryNode = {
          id: `major_category_${品牌}_${majorCategoryName}`,
          name: majorCategoryName,
          type: 'majorCategory',
          children: [],
          salesCount: 0
        };
        brandNode.children?.push(majorCategoryNode);
      }
      majorCategoryNode.salesCount = (majorCategoryNode.salesCount || 0) + 销售数量;

      // 中分类层级
      let mediumCategoryNode = majorCategoryNode.children?.find(child => child.name === mediumCategoryName);
      if (!mediumCategoryNode) {
        mediumCategoryNode = {
          id: `medium_category_${品牌}_${majorCategoryName}_${mediumCategoryName}`,
          name: mediumCategoryName,
          type: 'mediumCategory',
          children: [],
          salesCount: 0
        };
        majorCategoryNode.children?.push(mediumCategoryNode);
      }
      mediumCategoryNode.salesCount = (mediumCategoryNode.salesCount || 0) + 销售数量;

      // 小分类层级
      let minorCategoryNode = mediumCategoryNode.children?.find(child => child.name === minorCategoryName);
      if (!minorCategoryNode) {
        minorCategoryNode = {
          id: `minor_category_${品牌}_${majorCategoryName}_${mediumCategoryName}_${minorCategoryName}`,
          name: minorCategoryName,
          type: 'minorCategory',
          children: [],
          salesCount: 0
        };
        mediumCategoryNode.children?.push(minorCategoryNode);
      }
      minorCategoryNode.salesCount = (minorCategoryNode.salesCount || 0) + 销售数量;

      // 主SPU层级
      let mainSpuNode = minorCategoryNode.children?.find(child => child.name === 主SPU名称);
      if (!mainSpuNode) {
        mainSpuNode = {
          id: `main_spu_${品牌}_${majorCategoryName}_${mediumCategoryName}_${minorCategoryName}_${主SPU名称}`,
          name: 主SPU名称,
          type: 'mainSpu',
          children: [],
          salesCount: 0
        };
        minorCategoryNode.children?.push(mainSpuNode);
      }
      mainSpuNode.salesCount = (mainSpuNode.salesCount || 0) + 销售数量;

      // SPU层级
      let spuNode = mainSpuNode.children?.find(child => child.name === SPU名称);
      if (!spuNode) {
        spuNode = {
          id: `spu_${品牌}_${majorCategoryName}_${mediumCategoryName}_${minorCategoryName}_${主SPU名称}_${SPU名称}`,
          name: SPU名称,
          type: 'spu',
          children: [],
          salesCount: 0
        };
        mainSpuNode.children?.push(spuNode);
      }
      spuNode.salesCount = (spuNode.salesCount || 0) + 销售数量;

      // 产品层级
      const productNode: MindMapNode = {
        id: `product_${产品名称}`,
        name: 产品名称,
        type: 'product',
        salesCount: 销售数量,
        data: product
      };
      spuNode.children?.push(productNode);
    });

    // 按销售数量倒序排序第一层节点（品牌）
    const sortedBrands = Array.from(brandMap.values()).sort((a, b) => {
      // 按销售数量倒序排列
      const salesDiff = (b.salesCount || 0) - (a.salesCount || 0);
      if (salesDiff !== 0) return salesDiff;

      // 如果销售数量相同，按品牌名称排序
      return a.name.localeCompare(b.name);
    });

    // 递归排序所有层级的子节点
    const sortChildrenRecursively = (node: MindMapNode) => {
      if (node.children && node.children.length > 0) {
        // 先递归排序子节点
        node.children.forEach(child => sortChildrenRecursively(child));

        // 然后排序当前节点的子节点
        node.children.sort((a, b) => {
          // 按销售数量倒序排列
          const salesDiff = (b.salesCount || 0) - (a.salesCount || 0);
          if (salesDiff !== 0) return salesDiff;

          // 如果销售数量相同，按名称排序
          return a.name.localeCompare(b.name);
        });
      }
    };

    // 对每个品牌的子树进行递归排序
    sortedBrands.forEach(brand => sortChildrenRecursively(brand));

    return sortedBrands;
  };

  // 将脑图数据转换为ECharts树形数据格式
  const convertToEChartData = (mindMapData: MindMapNode[]): EChartTreeNode[] => {
    return mindMapData.map(brand => convertNodeToEChartNode(brand));
  };

  // 转换单个节点
  const convertNodeToEChartNode = (node: MindMapNode): EChartTreeNode => {
    const displayName = node.salesCount && node.salesCount > 0
      ? `${node.name} (${node.salesCount.toLocaleString()})`
      : node.name;

    const echartNode: EChartTreeNode = {
      name: displayName,
      value: node.salesCount || 0,
    };

    // 根据节点类型设置样式
    switch (node.type) {
      case 'brand':
        echartNode.itemStyle = { color: '#3B82F6' }; // 蓝色
        echartNode.label = {
          show: true,
          position: 'right',
          fontSize: 16,
          fontWeight: 'bold'
        };
        echartNode.lineStyle = { color: '#3B82F6', width: 3 };
        echartNode.collapsed = false; // 品牌节点默认展开
        break;
      case 'majorCategory':
        echartNode.itemStyle = { color: '#10B981' }; // 绿色
        echartNode.label = {
          show: true,
          position: 'right',
          fontSize: 15,
          fontWeight: '600'
        };
        echartNode.lineStyle = { color: '#10B981', width: 3 };
        echartNode.collapsed = false; // 大分类节点默认展开
        break;
      case 'mediumCategory':
        echartNode.itemStyle = { color: '#06B6D4' }; // 青色
        echartNode.label = {
          show: true,
          position: 'right',
          fontSize: 14,
          fontWeight: '600'
        };
        echartNode.lineStyle = { color: '#06B6D4', width: 2 };
        echartNode.collapsed = false; // 中分类节点默认展开
        break;
      case 'minorCategory':
        echartNode.itemStyle = { color: '#14B8A6' }; // 蓝绿色
        echartNode.label = {
          show: true,
          position: 'right',
          fontSize: 13,
          fontWeight: '600'
        };
        echartNode.lineStyle = { color: '#14B8A6', width: 2 };
        echartNode.collapsed = false; // 小分类节点默认展开
        break;
      case 'mainSpu':
        echartNode.itemStyle = { color: '#F59E0B' }; // 橙色
        echartNode.label = {
          show: true,
          position: 'right',
          fontSize: 12,
          fontWeight: '600'
        };
        echartNode.lineStyle = { color: '#F59E0B', width: 2 };
        echartNode.collapsed = true; // 主SPU节点默认折叠
        break;
      case 'spu':
        echartNode.itemStyle = { color: '#8B5CF6' }; // 紫色
        echartNode.label = {
          show: true,
          position: 'right',
          fontSize: 11,
          fontWeight: '500'
        };
        echartNode.lineStyle = { color: '#8B5CF6', width: 1 };
        break;
      case 'product':
        echartNode.itemStyle = { color: '#6B7280' }; // 灰色
        echartNode.label = {
          show: true,
          position: 'right',
          fontSize: 10,
          fontWeight: 'normal'
        };
        echartNode.lineStyle = { color: '#6B7280', width: 1 };
        // 为产品节点添加额外标识和数据
        echartNode.cursor = 'pointer'; // 鼠标悬停时显示手型
        if (node.data) {
          echartNode.productCode = node.data.dim_goods_no;
          echartNode.productName = node.data.产品名称;
          console.log('产品节点数据:', {
            name: node.name,
            productCode: echartNode.productCode,
            productName: echartNode.productName
          }); // 调试信息
        }
        break;
    }

    // 递归处理子节点
    if (node.children && node.children.length > 0) {
      echartNode.children = node.children.map(child => convertNodeToEChartNode(child));
    }

    return echartNode;
  };

  // 计算第五层节点数量并返回合适的高度（现在是7层结构：品牌→大分类→中分类→小分类→主SPU→SPU→产品）
  const calculateOptimalHeight = (mindMapData: MindMapNode[]): number => {
    let fifthLevelNodeCount = 0;

    // 遍历品牌 → 大分类 → 中分类 → 小分类 → 主SPU（第五层）
    mindMapData.forEach(brand => {
      brand.children?.forEach(majorCategory => {
        majorCategory.children?.forEach(mediumCategory => {
          mediumCategory.children?.forEach(minorCategory => {
            if (minorCategory.children) {
              fifthLevelNodeCount += minorCategory.children.length;
            }
          });
        });
      });
    });

    // 由于层级增多，需要调整高度计算策略
    const availableHeight = isFullscreen ? window.innerHeight - 120 : 800; // 可用高度
    const nodesPerScreen = 36; // 一屏显示20个节点
    const heightPerNode = availableHeight / nodesPerScreen; // 每个节点需要的高度

    // 为左侧品牌树结构和更多层级预留额外空间
    const reservedLeftSpace = 250; // 左侧预留空间（增加）
    const levelSpace = 150; // 额外的层级空间（新增层级需要更多空间）
    const calculatedHeight = reservedLeftSpace + levelSpace + (fifthLevelNodeCount * heightPerNode);

    // 设置最小和最大高度限制
    const minHeight = isFullscreen ? window.innerHeight - 120 : 800;
    const maxHeight = isFullscreen ? window.innerHeight - 120 : 10000; // 增加最大高度

    // 返回合适的高度值
    const optimalHeight = Math.max(minHeight, Math.min(calculatedHeight, maxHeight));

    console.log(`第五层（主SPU）节点数量: ${fifthLevelNodeCount}, 每节点高度: ${heightPerNode.toFixed(2)}px, 计算高度: ${calculatedHeight}, 最终高度: ${optimalHeight}`);

    return optimalHeight;
  };

  // 初始化和更新ECharts图表
  const updateChart = () => {
    if (!chartRef.current || mindMapData.length === 0) return;

    // 如果图表实例已存在，先销毁
    if (chartInstance.current) {
      chartInstance.current.dispose();
    }

    // 获取容器尺寸
    const container = chartRef.current;
    const width = container.offsetWidth;
    const height = container.offsetHeight;

    // 创建新的图表实例，指定确切尺寸
    chartInstance.current = echarts.init(chartRef.current, null, {
      width: width,
      height: height
    });

    const echartData = convertToEChartData(mindMapData);

    console.log('转换后的ECharts数据:', echartData); // 调试信息
    console.log('品牌数据长度:', mindMapData.length); // 检查品牌数量
    if (mindMapData.length > 0) {
      console.log('第一个品牌的结构:', mindMapData[0]); // 检查第一个品牌的子节点
    }

    const option = {
      tooltip: {
        trigger: 'item',
        triggerOn: 'mousemove',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ccc',
        borderWidth: 1,
        textStyle: {
          color: '#333'
        },
        formatter: function(params: any) {
          const fullName = params.name;
          const value = params.value || 0;
          const level = params.treePathInfo ? params.treePathInfo.length - 1 : 1;
          const levelNames = ['品牌', '大分类', '中分类', '小分类', '主SPU', 'SPU', '产品'];
          const levelName = levelNames[level] || '未知';

          // 从名称中提取纯名称（去掉括号和数量）
          const pureName = fullName.replace(/\s*\(\d[\d,]*\)$/, '');

          // 如果是产品节点，添加点击提示
          const clickHint = level === 6 ? '<div style="color: #10B981; font-size: 11px; margin-top: 6px; font-style: italic;">👆 点击查看库存预测</div>' : '';

          return `<div style="padding: 8px;">
            <div style="font-weight: bold; margin-bottom: 4px;">${pureName}</div>
            <div style="color: #666; font-size: 12px;">${levelName}</div>
            <div style="color: #3B82F6; margin-top: 4px;">销量: ${value.toLocaleString()}</div>
            ${clickHint}
          </div>`;
        }
      },
      series: [
        {
          type: 'tree',
          data: echartData,
          layout: 'orthogonal', // 使用正交布局
          orient: 'LR', // 从左到右
          roam: true, // 启用缩放和平移
          zoom: 1,
          top: '0%',
          left: '1%',
          bottom: '2%',
          right: '25%',
          initialTreeDepth: 4, // 初始展开三层（品牌→大分类→中分类）
          // 强制展开前3层
          collapseInterval: 1,
          expandAndCollapse: true,
          symbolSize: 20, // 减小节点大小
          // 调整节点间距，让连线更短
          nodePadding: 20, // 固定间距，让连线更短
          label: {
            show: true,
            position: 'right',
            fontSize: 14,
            color: '#000'
          },
          emphasis: {
            focus: 'descendant',
            scale: true,
            scaleSize: 20, // 鼠标悬停时放大节点
            label: {
              show: true,
              fontSize: 14,
              fontWeight: 'bold'
            }
          },
          blur: {
            itemStyle: {
              opacity: 0.4
            },
            label: {
              opacity: 0.4
            },
            lineStyle: {
              opacity: 0.4
            }
          },
          animationDuration: 550,
          animationDurationUpdate: 750,
          lineStyle: {
            color: '#999',
            width: 2,
            curveness: 0.3, // 减小曲线度，让连线更直更短
            type: 'curve' // 使用曲线连接
          },
          // 设置节点间距和层级距离
          nodeScaleRatio: 1, // 恢复正常节点比例
          // 减少层级间的距离
          levelSeparation: 30, // 层级间的水平距离，让连线更短
          itemStyle: {
            borderWidth: 2,
            borderColor: '#fff',
            shadowBlur: 8,
            shadowColor: 'rgba(0, 0, 0, 0.1)',
            shadowOffsetX: 2,
            shadowOffsetY: 2
          }
        }
      ]
    };

    chartInstance.current.setOption(option);

    // 强制展开前3层
    setTimeout(() => {
      if (chartInstance.current) {
        // 展开前3层节点（品牌→大分类→中分类）
        chartInstance.current.dispatchAction({
          type: 'expand',
          dataIndex: [0, 1, 2,3] // 展开前3层
        });
      }
    }, 500);

    // 启用缩放和平移
    chartInstance.current.on('dataZoom', function() {
      console.log('图表已缩放');
    });

    // 添加节点点击事件监听
    chartInstance.current.on('click', function(params: any) {
      console.log('点击事件触发:', params); // 调试信息

      // 检查点击的是否是产品节点
      if (params.data && params.data.productCode) {
        // 获取点击的节点数据
        const clickedNode = params.data;

        console.log('检测到产品节点点击:', clickedNode); // 调试信息

        // 构建目标URL - 动态获取当前域名
        const currentOrigin = window.location.origin;
        const targetUrl = `${currentOrigin}/InventoryForecast?productCode=${encodeURIComponent(clickedNode.productCode)}`;

        // 在新标签页中打开
        window.open(targetUrl, '_blank');

        console.log(`跳转到库存预测页面，产品: ${clickedNode.productName || clickedNode.name}, 产品编码: ${clickedNode.productCode}`);
      } else {
        console.log('点击的不是产品节点或缺少产品编码:', params.data);
      }
    });

    // 监听窗口大小变化
    const handleResize = () => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  };

  // 品牌选择处理函数
  const handleBrandChange = (brandCode: string) => {
    console.log('品牌选择变化:', brandCode);
    setSelectedBrandCode(brandCode);
  };

  // 处理产品节点点击事件
  const handleProductClick = (product: OnSaleSpuProduct) => {
    const productCode = product.dim_goods_no;
    if (productCode) {
      // 构建目标URL - 动态获取当前域名
      const currentOrigin = window.location.origin;
      const targetUrl = `${currentOrigin}/InventoryForecast?productCode=${encodeURIComponent(productCode)}`;

      // 在新标签页中打开
      window.open(targetUrl, '_blank');

      console.log(`跳转到库存预测页面，产品编码: ${productCode}`);
    } else {
      console.warn('产品编码为空，无法跳转');
    }
  };

  // 切换全屏模式
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // 缩放控制
  const zoomIn = () => {
    if (chartInstance.current) {
      const option = chartInstance.current.getOption();
      const currentZoom = option.series?.[0]?.zoom || 1;
      chartInstance.current.setOption({
        series: [{
          zoom: currentZoom * 1.2
        }]
      });
    }
  };

  const zoomOut = () => {
    if (chartInstance.current) {
      const option = chartInstance.current.getOption();
      const currentZoom = option.series?.[0]?.zoom || 1;
      chartInstance.current.setOption({
        series: [{
          zoom: Math.max(currentZoom * 0.8, 0.5)
        }]
      });
    }
  };

  const resetZoom = () => {
    if (chartInstance.current) {
      chartInstance.current.dispatchAction({
        type: 'restore'
      });
    }
  };


  // 更新图表和高度
  useEffect(() => {
    if (mindMapData.length > 0) {
      // 重新计算高度（当全屏模式变化时）
      const optimalHeight = calculateOptimalHeight(mindMapData);
      setChartHeight(optimalHeight);

      updateChart();
    }
  }, [mindMapData, isFullscreen]);

  // 监控品牌选择变化
  useEffect(() => {
    console.log('selectedBrandCode 变化:', selectedBrandCode);
    console.log('对应品牌名称:', BRAND_OPTIONS.find(b => b.code === selectedBrandCode)?.name);
  }, [selectedBrandCode]);

  // 初始化和品牌变化时获取数据
  useEffect(() => {
    fetchOnSaleSpuProducts();
  }, [selectedBrandCode]); // 当品牌选择变化时重新获取数据

  // 清理图表实例
  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
      }
    };
  }, []);

  return (
    <div className={cn(
      "min-h-screen bg-gray-50",
      isFullscreen ? "fixed inset-0 z-50 bg-white" : ""
    )}>
      {/* 页面标题区域 */}
      {!isFullscreen && (
        <div className="p-6">
          <div className="flex items-center gap-6">
            {/* 页面标题 */}
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">在售商品情况</h1>
              <p className="text-gray-600 mt-1">
                在售商品层级关系，支持品牌筛选
              </p>
            </div>

            {/* 品牌选择区域 */}
            <div className="flex-1 max-w-xs">
              <select
                value={selectedBrandCode}
                onChange={(e) => handleBrandChange(e.target.value)}
                disabled={loading}
                className="w-full h-[42px] bg-white border border-gray-300 hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 shadow-sm rounded-md px-3 py-2 text-sm"
              >
                <option value="" disabled>请选择品牌</option>
                {BRAND_OPTIONS.map((brand) => (
                  <option key={brand.code} value={brand.code}>
                    {brand.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* 脑图展示区域 */}
      <div className={cn(
        "px-6 pb-6",
        isFullscreen ? "p-4 h-screen" : ""
      )}>
        <div className={cn(
          "bg-gradient-to-br from-blue-50 via-white to-purple-50 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 border border-blue-100",
          isFullscreen ? "h-full p-4" : "p-8"
        )}>
          {/* 头部标题和控制按钮 */}
          <div className="flex items-center justify-between mb-6">
            <h3 className={cn(
              "font-bold flex items-center gap-3 text-slate-800",
              isFullscreen ? "text-xl" : "text-2xl"
            )}>
              <Package className={cn("text-blue-600", isFullscreen ? "w-5 h-5" : "w-6 h-6")} />
              在售商品(按分类层级展示)
            </h3>

            {/* 控制按钮 */}
            <div className="flex items-center gap-2">
              {/* 缩放控制按钮 */}
              <div className="flex items-center bg-white rounded-lg shadow-md border border-gray-200">
                <button
                  onClick={zoomIn}
                  className="p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-l-lg transition-all duration-300"
                  title="放大"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
                <button
                  onClick={zoomOut}
                  className="p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-all duration-300"
                  title="缩小"
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <button
                  onClick={resetZoom}
                  className="p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-r-lg transition-all duration-300"
                  title="重置视图"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>

              {/* 全屏控制按钮 */}
              <button
                onClick={toggleFullscreen}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 border border-gray-200",
                  "text-gray-700 hover:text-gray-900"
                )}
              >
                {isFullscreen ? (
                  <>
                    <Minimize2 className="w-4 h-4" />
                    退出全屏
                  </>
                ) : (
                  <>
                    <Maximize2 className="w-4 h-4" />
                    全屏显示
                  </>
                )}
              </button>
            </div>
          </div>

          {/* 图表容器 */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-12 h-12 animate-spin text-blue-500" />
              <span className="ml-4 text-gray-600 text-lg">数据加载中...</span>
            </div>
          ) : mindMapData.length === 0 ? (
            <div className="text-center py-20 text-gray-500" style={{ height: `${isFullscreen ? window.innerHeight - 120 : 400}px` }}>
              <Package className="w-16 h-16 mx-auto mb-6 text-gray-300" />
              <p className="text-lg">暂无数据</p>
            </div>
          ) : (
            <div
              ref={chartRef}
              className={cn(
                "w-full transition-all duration-300"
              )}
              style={{ height: `${chartHeight}px` }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default OnSaleSpuProducts;